import { inject, singleton } from 'tsyringe';
import { OWExtensionsDelegate, OWGameListenerDelegate } from '../../odk-ts';
import {
  kLaunchEventDockLaunch,
  kLaunchEventGameLaunch,
  kLaunchEventOverwolfStart,
  kUrlParamGameId,
  kUrlParamSource,
} from '../../odk-ts/ow-extensions';
import { LoggerService } from 'app-core/src/services/logger.service';
import { UtilsService } from 'app-core/src/services/utils.service';
import { FunctionPropertyNames } from 'app-core/src/types';
import { OWExtensionsListenerWrapper } from '../ow-odk-wrappers/ow-extensions-listener-wrapper';
import { DelegateProvider } from 'app-core/src/interfaces/delegate-provider.interface';
import {
  kDITOWGameListenerWrapper,
  kDITOWGamesWrapper,
} from 'app-core/src/constants/injectionTokens';
import { OWGamesWrapper } from '../ow-odk-wrappers/ow-games-wrapper';
import { IOwGamesWrapper } from 'app-core/src/interfaces/ow-wrappers/ow-games-wrapper.interface';

// -----------------------------------------------------------------------------
export interface IAppEntryPointDelegate {
  onLaunchedWithStartup?();
  onLaunchedWithDock?();
  onLaunchedWithGame?(gameId: number);
  onGameStarted?(gameId: number);
  onGameEnded?(gameId: number);
}

// -----------------------------------------------------------------------------
// TODO(twolf): This currently doesn't support onLaunchedStarted event - for the
// edge case of having the League Launcher launched while the app is running.
// To support this - we need to integrate with |OWLauncherEvents|
@singleton()
export class AppEntryPointService
  implements
    DelegateProvider<IAppEntryPointDelegate>,
    OWExtensionsDelegate,
    OWGameListenerDelegate
{
  private readonly logger = LoggerService.getCategory('AppEntryPointService');
  private readonly listeners: Set<IAppEntryPointDelegate>;

  // ---------------------------------------------------------------------------
  constructor(
    private readonly utilsService: UtilsService,
    private readonly owExtensionsListener: OWExtensionsListenerWrapper,
    @inject(kDITOWGameListenerWrapper)
    private readonly owGameListener: DelegateProvider<OWGameListenerDelegate>,
    @inject(kDITOWGamesWrapper)
    private readonly gamesWrapper: IOwGamesWrapper,
  ) {
    this.listeners = new Set<IAppEntryPointDelegate>();
  }

  // ---------------------------------------------------------------------------
  async initialize() {
    await this.checkLaunchParam();
    this.owExtensionsListener.addListener(this);
    this.owGameListener.addListener(this);
  }

  // ---------------------------------------------------------------------------
  // DelegateProvider
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  public addListener(listener: IAppEntryPointDelegate) {
    this.listeners.add(listener);
  }

  // ---------------------------------------------------------------------------
  public removeListener(listener: IAppEntryPointDelegate) {
    this.listeners.delete(listener);
  }

  // ---------------------------------------------------------------------------
  private async checkLaunchParam() {
    const source = this.utilsService.getUrlParam(kUrlParamSource);
    const gameId = this.utilsService.getUrlParam(kUrlParamGameId);

    const runningGameInfo = await this.gamesWrapper.getRunningGameInfo();
    if (runningGameInfo) {
      this.logger.info('Running Game Info', JSON.stringify(runningGameInfo));
    } else {
      this.logger.info(`Started with game ID: ${gameId}`);
    }

    const launchedGameId = runningGameInfo?.classId.toString() ?? gameId;


    this.handleLaunchSource(source, launchedGameId, true);
  }

  // ---------------------------------------------------------------------------
  private handleLaunchSource(source: string, param: string, launched: boolean) {
    const msgPrefix = launched ? 'Launched' : 'Received event';
    this.logger.info(`${msgPrefix} source: ${source} param: ${param || 'n/a'}`);

    const [listenerEventName, listenerEventParam] =
      this.calcListenerEventFromLaunchSource(source, param);

    this.triggerListenerEvent(listenerEventName, listenerEventParam);
  }

  // ---------------------------------------------------------------------------
  private calcListenerEventFromLaunchSource(
    source: string,
    param: string,
  ): [FunctionPropertyNames<IAppEntryPointDelegate>, any] {
    switch (source) {
      case kLaunchEventDockLaunch:
        return ['onLaunchedWithDock', null];
      case kLaunchEventOverwolfStart:
        return ['onLaunchedWithStartup', null];
      case kLaunchEventGameLaunch:
        return ['onLaunchedWithGame', parseInt(param, 10)];
    }

    return [null, null];
  }

  // ---------------------------------------------------------------------------
  private async triggerListenerEvent(
    eventName: FunctionPropertyNames<IAppEntryPointDelegate>,
    param: any,
  ) {
    if (!eventName) {
      return;
    }

    for (const listener of this.listeners) {
      if (!listener[eventName]) {
        continue;
      }

      await listener[eventName](param);
    }
  }

  // ---------------------------------------------------------------------------
  // OWExtensionsDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  onAppLaunchTriggered(event: overwolf.extensions.AppLaunchTriggeredEvent) {
    switch (event.origin) {
      case kLaunchEventDockLaunch:
        this.handleLaunchSource(event.origin, event.parameter, false);
        break;
      case kLaunchEventGameLaunch: {
        const gameIdParamPrefix = `${kUrlParamGameId}=`;
        const gameIdPart = event.parameter.substring(gameIdParamPrefix.length);
        this.triggerListenerEvent('onGameStarted', parseInt(gameIdPart, 10));
        break;
      }
    }
  }

  // ---------------------------------------------------------------------------
  // OWGamesDelegate
  // ---------------------------------------------------------------------------
  // NOTE(twolf): We do not listen for onGameStarted because then we run into
  // situations where we have double events (e.g. launched with a game +
  // game started). Instead, we handle onGameStarted in |onAppLaunchTriggered|

  // ---------------------------------------------------------------------------
  onGameTerminated(info: overwolf.games.GameInfoUpdatedEvent) {
    this.triggerListenerEvent('onGameEnded', info.gameInfo.classId);
  }
}
